import {
  alpha,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Skeleton,
  styled,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ApiService from "../../utils/fetch";
import { MessagePopup } from "../../Components/Common/messagePopup/MessagePopup";
import ButtonComponent from "../../Components/Common/button/Button";
import DataTable from "../../Components/Common/datatable/DataTable";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import SaveIcon from "@mui/icons-material/Save";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import ReminderDialog from "./ReminderDialog";
import EditIcon from "@mui/icons-material/Edit";
import EditReminderDialog from "./EditReminderDialog";
import InfoPopover from "../../Components/Common/poper/InfoPopover";
import { set } from "lodash";
import { useSelector } from "react-redux";


const ReminderForm = () => {
  const gridRef = useRef(null);
  const userId = useSelector((state) => state.user?.userData?.id);
  const currentUserRoles = useSelector((state) => state.user.roles);
  const isAdmin = currentUserRoles.includes("Admin");

  // Form state variables
  const [daysNumber, setDaysNumber] = useState("");
  const [dataElement, setDataElement] = useState(null);
  const [forecastTerm, setForecastTerm] = useState(null);
  const [reminderPeriod, setReminderPeriod] = useState(null);

  const [selectedBottler, setSelectedBottler] = useState(null);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedBottlerOwnerCode, setSelectedBottlerOwnerCode] = useState("");
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedCustomerCountry, setSelectedCustomerCountry] = useState([]);

  const [dataElementsOption, setDataElementsOption] = useState([]);
  const [reminderPeriodOptions, setReminderPeriodOptions] = useState([]);
  const [reminderPeriodWithDeadlineOptions, setReminderPeriodWithDeadlineOptions] = useState([]);
  const [reminderPeriodWithoutDeadlineOptions, setReminderPeriodWithoutDeadlineOptions] = useState([]);

  const [roleOptions, setRoleOptions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tableRoleOptions, setTableRoleOptions] = useState([]);

  const [bottlerOption, setBottlerOption] = useState([]);
  const [partnerOption, setPartnerOption] = useState([]);
  const [plantOption, setPlantOption] = useState([]);
  const [customerCountryOption, setCustomerCountryOption] = useState([]);

  const [adminAloneEditable, setAdminAloneEditable] = useState(false);

  const [customerCountryNoOptionsText, setCustomerCountryNoOptionsText] = useState(
    "Select Bottler to view Customer Country",
  );
  const [searchCustomerCountryText, setSearchCustomerCountryText] = useState("");
  const [searchForecastPartnerText, setSearchForecastPartnerText] = useState("");
  const [searchText, setSearchText] = useState("");

  const [plantNoOptionsText, setPlantNoOptionsText] = useState("Select Forecast Partner to view Plants");

  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);

  const [emails, setEmails] = useState("");
  const [emailError, setEmailError] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editRowData, setEditRowData] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [open, setOpen] = useState(false);
  const [reminderDetails, setReminderDetails] = useState({});

  const forecastTermOptions = ["Long Term", "Short Term"];

  const isForecastTermEnabled = dataElement === "Bottler Concentrate Forecast";

  useEffect(() => {
    fetchReminderOption();
    fetchDataElementOption();
    fetchTableData();
    fetchRoleOption();
  }, []);

  const validateEmails = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const invalidEmails = value
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0 && !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setEmailError(`Invalid email(s): ${invalidEmails.join(", ")}`);
      return false;
    }

    setEmailError("");
    return true;
  };

  const openEditDialog = async (row) => {
    const id = row.id;
    try {
      const response = await ApiService.get(
        `/v1/reminder-configurations/getById/${id}`,
      );
      setEditRowData(response.data);
      setEditOpen(true);
    } catch (error) {
      console.error("Failed to update reminder configuration:", error);
    }
  };

  const closeEditDialog = () => {
    setEditOpen(false);
    setEditRowData(null);
  };

  // Fetch DataElement Options
  const fetchDataElementOption = async () => {
    try {
      const response = await ApiService.get(`/v1/data-element/get`);
      setDataElementsOption(response.data || []);
    } catch (error) {
      console.error("Failed to fetch forecast term options:", error);
      setDataElementsOption([]);
    }
  };

  // Fetch Reminder Period Options
  const fetchReminderOption = async () => {
    try {
      const response = await ApiService.get(
        `/v1/reminder-configurations/get-reminder-periods`,
      );
      setReminderPeriodWithDeadlineOptions(response.data || []);

      const withoutDeadlineOptions = (response.data || []).filter(
        (option) => !option.code.startsWith("DEADLINE")
      );
      setReminderPeriodWithoutDeadlineOptions(withoutDeadlineOptions);

    } catch (error) {
      console.error("Failed to fetch reminder period options:", error);
      setReminderPeriodWithDeadlineOptions([]);
      setReminderPeriodWithoutDeadlineOptions([]);
    }
  };

  //Table Data
  const fetchTableData = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(
        `/v1/reminder-configurations/getAll`,
      );
      setFilteredData(response.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };


  // Clear all fields to default state
  const handleClear = () => {
    setDaysNumber("");
    setDataElement(null);
    setForecastTerm(null);
    setReminderPeriod(null);
    setIsSubmitting(false);
    setRoles([]);
    setEmails("");
    setSelectedBottler(null);
    setSelectedBottlerOwnerCode(null);
    setSelectedPartners([]);
    setSelectedPlants([]);
    setSelectedCustomerCountry([]);
  };

  // Validate form data - returns array of missing fields
  const validateFormData = () => {
    const missing = [];

    if (!dataElement) missing.push("Data Elements");
    if (!reminderPeriod) missing.push("Reminder Period");
    if (!daysNumber) missing.push("Number of Days");
    if (!roles || roles.length === 0) missing.push("Roles");

    // Forecast Term is required only for Bottler Concentrate Forecast
    if (isForecastTermEnabled && !forecastTerm) {
      missing.push("Forecast Term");
    }

    if (!selectedBottler) {
      missing.push("Bottler");
    }

    if (dataElement === "Bottler Concentrate Forecast") {
      if (!selectedPartners || selectedPartners.length === 0) {
        missing.push("Forecast Partners");
      }

      if (!selectedPlants || selectedPlants.length === 0) {
        missing.push("Plants");
      }


    } else {
      if (!selectedCustomerCountry || selectedCustomerCountry.length === 0) {
        missing.push("Customer Country");
      }

    }

    // Validate Number of Days (0-14)
    const days = parseInt(daysNumber, 10);
    if (daysNumber && (isNaN(days) || days < 0 || days > 14)) {
      missing.push("Number of Days (must be between 0 and 14)");
    }

    return missing;
  };

  // Handle Submit
  const handleSubmit = async () => {
    if (isSubmitting) return;
    const missing = validateFormData();

    if (missing.length > 0) {
      MessagePopup({
        icon: "error",
        title: "Missing Required Fields",
        message: `${missing.join(", ")}`,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await processDataSubmission();
    } catch (error) {
      console.error("Submission failed:", error);
      MessagePopup({
        icon: "error",
        title: "Submission Failed",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Actual submission logic
  const processDataSubmission = async () => {
    const isBottlerForecast = dataElement === "Bottler Concentrate Forecast";

    const payload = {
      dataElement: dataElement,
      forecastTerm: isBottlerForecast
        ? forecastTerm === "Short Term"
          ? 1
          : forecastTerm === "Long Term"
            ? 2
            : null
        : null,

      reminderPeriod: reminderPeriod?.code || "",
      ndays: Number(daysNumber),
      active: true,
      roles: roles.map((r) => r.code),
      userEmails: emails, // comma-separated string
      bottler: selectedBottler,
      bottlerOwnerCode: selectedBottlerOwnerCode,
      customerCountryList: selectedCustomerCountry.map(
        item => item.profitCenterDetails.split("-")[0]
      ),
      customerInfoList: Object.values(
        (selectedPlants ?? []).reduce((acc, { customerDetails, locationDetails }) => {
          if (!acc[customerDetails]) {
            acc[customerDetails] = {
              customerID: customerDetails,
              locationIds: []
            };
          }

          acc[customerDetails].locationIds.push(locationDetails.split("-")[0]);

          return acc;
        }, {})
      ), adminAloneEditable: adminAloneEditable,
    };

    console.log("Submitting payload:", payload);

    try {
      await ApiService.post(`/v1/reminder-configurations/save`, payload);

      MessagePopup({
        icon: "success",
        title: "Success",
        message: "Reminder Notification Successfully Submitted",
      });

      handleClear();
      fetchTableData();
    } catch (error) {
      const backendMessage = error?.response?.data?.message || "Failed to Save";

      MessagePopup({
        icon: "error",
        title: "Error",
        message: backendMessage,
      });
    }
  };

  const getForecastTermCode = (forecastTerm) => {
    if (forecastTerm === 1 || forecastTerm === "Short Term") return 1;
    if (forecastTerm === 2 || forecastTerm === "Long Term") return 2;
    return "";
  };

  const handleOpen = async (rowData) => {
    const id = rowData.id;
    try {
      const response = await ApiService.get(
        `/v1/reminder-configurations/getById/${id}`,
      );
      setReminderDetails(response.data);
      setOpen(true);
    } catch (error) {
      console.error("Failed to update reminder configuration:", error);
    }
  };
  const columnDefinition = [
    {
      headerName: "",
      checkboxSelection: true,
      width: 40,
      minWidth: 40,
      // pinned: "left",
      suppressMenu: true,
      suppressMovable: true,
      headerCheckboxSelection: true,
    },
    {
      field: "dataElement",
      headerName: "Data Element",
      sortable: true,
      unSortIcon: true,
      width: 195,
      minWidth: 195,
      valueGetter: (params) => {
        const { dataElement } = params.data;
        return `${dataElement || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "bottler",
      headerName: "Bottler",
      sortable: true,
      unSortIcon: true,
      width: 195,
      minWidth: 195,
      valueGetter: (params) => {
        const { dataElement } = params.data || {};
        console.log("params:", params);
        return `${dataElement || "-"}`;
      },
      cellRenderer: (params) => {
        const display = params.data.bottler || "-";
        console.log(" cellRendererparams:", params);
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "forecastTerm",
      headerName: "Forecast Term",
      sortable: true,
      unSortIcon: true,
      width: 200,
      minWidth: 200,

      valueGetter: (params) => {
        const value = params.data?.forecastTerm;
        if (value === 1) return "Short Term";
        if (value === 2) return "Long Term";
        return value || "";
      },

      cellRenderer: (params) => {
        const displayValue =
          params.value === "Short Term" || params.value === "Long Term"
            ? params.value
            : "";

        return (
          <Tooltip title={displayValue} arrow>
            <div>{displayValue}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          { filter: "agTextColumnFilter" },
          { filter: "agSetColumnFilter" },
        ],
      },
    },
    {
      field: "reminderPeriodDisplayName",
      headerName: "Reminder",
      sortable: true,
      unSortIcon: true,
      width: 242,
      minWidth: 242,
      // hide: true,
      valueGetter: (params) => {
        const { reminderPeriodDisplayName } = params.data;
        return `${reminderPeriodDisplayName || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "roles",
      headerName: "Roles",
      sortable: true,
      unSortIcon: true,
      width: 230,
      minWidth: 230,
      flex: 1,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          { filter: "agTextColumnFilter" },
          { filter: "agSetColumnFilter" },
        ],
      },
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.4",
        paddingTop: "8px",
        paddingBottom: "8px",
      },
      valueGetter: (params) => {
        const roles = params?.data?.roles;

        if (!roles) return "";

        // If roles is an array
        if (Array.isArray(roles)) {
          return roles.map((role) => String(role).trim()).join("\n");
        }

        // If roles is a string
        if (typeof roles === "string") {
          return roles
            .split(",")
            .map((role) => role.trim())
            .join("\n");
        }

        // Fallback for any other type
        return String(roles);
      },
      cellRenderer: (params) => {
        if (!params.value) return null;

        const roles = params.value.split("\n");

        return (
          <div style={{ padding: "4px 0" }}>
            {roles.map((role, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "4px",
                  fontSize: "0.875rem",
                }}
              >
                {role}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: "ndays",
      headerName: "No of Days",
      sortable: true,
      unSortIcon: true,
      width: 170,
      minWidth: 170,
      // hide: true,
      valueGetter: (params) => {
        const { ndays } = params.data;
        return `${ndays || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      field: "userEmails",
      headerName: "User Email",
      sortable: true,
      unSortIcon: true,
      width: 242,
      minWidth: 242,
      // hide: true,
      valueGetter: (params) => {
        const { userEmails } = params.data;
        return `${userEmails || ""}`;
      },
      cellRenderer: (params) => {
        const display = params.value;
        return (
          <Tooltip title={<span>{display}</span>} arrow>
            <div>{display}</div>
          </Tooltip>
        );
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
          },
          {
            filter: "agSetColumnFilter",
          },
        ],
      },
    },
    {
      headerName: "View Details",
      sortable: false,
      width: 140,
      minWidth: 140,
      resizable: false,

      cellRenderer: (params) => {
        return (
          <Tooltip title="View Details" arrow>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                cursor: "pointer",
              }}
              onClick={() => handleOpen(params.data)}
            >
              <VisibilitySharpIcon fontSize="small" />
            </div>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Edit",
      sortable: false,
      width: 140,
      minWidth: 140,
      resizable: false,

      cellRenderer: (params) => (
        (isAdmin || !params.data.adminAloneEditable) && (<Tooltip title="Edit" arrow>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              cursor: "pointer",
            }}
            onClick={() => openEditDialog(params.data)}
          >
            <EditIcon fontSize="small" />
          </div>
        </Tooltip>)
      ),
    },
  ];

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();

      // Adding a resize event listener to handle zoom changes
      window.addEventListener("resize", () => {
        gridApi.sizeColumnsToFit();
        gridApi.refreshCells();
      });
    } else {
      console.error("Grid API is not available.");
    }
  };

  const onCellValueChanged = (params) => {
    if (
      ["forecastTerm", "reminderPeriod", "ndays"].includes(params.colDef.field)
    ) {
      params.data.isEdited = true; // Mark row as changed

      // Force refresh to show Save button immediately
      if (gridRef.current?.api) {
        gridRef.current.api.refreshCells({ force: true });
      }
    }
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedRows(selected);
  };

  //fetch bottler selection
  const fetchBottlerSelection = async (url) => {
    try {
      const response = await ApiService.get(url);

      setBottlerOption(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching bottler selection:", error);
      setBottlerOption([]);
    }
  };

  //handle data element change
  const handleDataElementChange = (event, value) => {
    setDataElement(value || null);

    // Reset values
    setSelectedBottler(null);
    setSelectedPartners([]);
    setSelectedBottlerOwnerCode(null);
    setSelectedPlants([]);
    setSelectedCustomerCountry([]);

    // Reset options
    setBottlerOption([]);
    setPartnerOption([]);
    setPlantOption([]);
    setCustomerCountryOption([]);

    setForecastTerm(
      value === "Bottler Concentrate Forecast" ? forecastTerm : null
    );

    setReminderPeriodOptions(value === "Bottler Concentrate Forecast" ?
      reminderPeriodWithDeadlineOptions : reminderPeriodWithoutDeadlineOptions);

    const endpointMap = {
      "Bottler Concentrate Forecast":
        `/v1/admin/userManagement/bottler-by-userid?userId=${userId}`,

      "Bottler Market Forecast":
        `/v1/user-pc-country-access/get-bottlers-by-user-id/marketforecast?userId=${userId}`,

      "FG Inventory":
        `/v1/user-pc-country-access/get-bottlers-by-user-id/fgInventory?userId=${userId}`,

      "Bottler Bussiness Plan":
        `/v1/user-pc-country-access/get-bottlers-by-user-id/businessPlan?userId=${userId}`,

      "Concentrate Inventory":
        `/v1/user-pc-country-access/get-bottlers-by-user-id/concentrateInventory?userId=${userId}`,

      "Production Requirement":
        `/v1/user-pc-country-access/get-bottlers-by-user-id/productionRequirement?userId=${userId}`,
    };

    const url = endpointMap[value];
    if (url) {
      fetchBottlerSelection(url);
    }
  };

  //Fetch bottler owner code
  const fetchBottlerOwnerCode = async (bottler) => {
    try {
      const response = await ApiService.get(
        `/v1/user-pc-country-access/get-bottler-owner-code-by-bottler?bottler=${bottler}`
      );

      setSelectedBottlerOwnerCode(
        Array.isArray(response.data) ? response.data.join(", ") : ""
      );

      const endpointMap = {
        "Bottler Market Forecast":
          "/v1/reminder-configurations/get-country-by-bottler-owner-code/marketforecast",

        "FG Inventory":
          "/v1/reminder-configurations/get-country-by-bottler-owner-code/fgInventory",

        "Bottler Bussiness Plan":
          "/v1/reminder-configurations/get-country-by-bottler-owner-code/businessPlan",

        "Concentrate Inventory":
          "/v1/reminder-configurations/get-country-by-bottler-owner-code/concentrateInventory",

        "Production Requirement":
          "/v1/reminder-configurations/get-country-by-bottler-owner-code/productionRequirement",
      };

      const url = endpointMap[dataElement];


      //fetch customer country options
      fetchCustomerCountryOptions(url, response.data);

    } catch (error) {
      console.error("Error fetching bottler owner code:", error);
      setSelectedBottlerOwnerCode("");
    }
  };

  const fetchPlantOptions = async (partners) => {
    const customerId = partners.map((item) => item.split("-")[0]);

    const payloadData = {
      userId: userId,
      customerIds: customerId,
    };

    try {
      const response = await ApiService.post(
        `v1/reminder-configurations/location-selection-by-customer`,
        payloadData,
      );
      if (response.data && response.data.length > 0) {
        const updatedPlants = response.data.map((plant) => ({
          ...plant,
          locationDetails: `${plant.locationDetails}(${plant.customerDetails})`,
        }));
        setPlantOption(updatedPlants);
        setPlantNoOptionsText("Select Forecast Partner to view Plants"); // Reset to default message
      } else {
        setPlantOption([]); // Clear plant options
        setPlantNoOptionsText("No Plants for Select Forecast Partner"); // Update message for empty array
      }
    } catch (error) {
      console.error("Error fetching plant options:", error);
      setPlantOption([]); // Clear plant options in case of error
      setPlantNoOptionsText("Error fetching plant options"); // Optional error message
    }
  };

  //Forecast Partner handle
  const handleForecastPartnerChange = (event, newValue) => {
    const isSelectAllClicked = newValue.some((item) => item.isSelectAll);

    setSelectedPlants([]);
    setPlantOption([]);

    if (isSelectAllClicked) {
      if (selectedPartners.length === partnerOption.length) {
        setSelectedPartners([]);
      } else {
        setSelectedPartners(partnerOption)
        fetchPlantOptions(partnerOption);
      }

    } else {
      setSelectedPartners(newValue || []);
      fetchPlantOptions(newValue);
    }
  };

  //Handle bottler change
  const handleBottlerChange = (event, value) => {
    setSelectedBottler(value || null);

    //clear dependent selections
    setSelectedPartners([]);
    setSelectedPlants([]);
    setSelectedCustomerCountry([]);
    setSelectedBottlerOwnerCode("");

    //clear dependent options
    setCustomerCountryOption([]);
    setPartnerOption([]);
    setPlantOption([]);

    if (value && value.length > 0) {
      if (dataElement === "Bottler Concentrate Forecast") {
        fetchBottlerConcentrateForecastPartnerOptions(value);
      } else {
        fetchBottlerOwnerCode(value);
      }
    } else {
      setSelectedBottlerOwnerCode("");
    }
  };

  const selectAllForecastPartnerOption = {
    forecastPartnerDetails: "All Forecast Partner",
    isSelectAll: true,
  };

  const optionsWithSelectAllForecastPartners =
    partnerOption.length > 0
      ? [selectAllForecastPartnerOption, ...partnerOption]
      : [];

  const selectAllOption = {
    locationDetails: "All Plants",
    isSelectAll: true,
  };

  const optionsWithSelectAll =
    plantOption.length > 0 ? [selectAllOption, ...plantOption] : [];

  const selectAllCustomerCountryOption = {
    profitCenterDetails: "All Customer Country",
    isSelectAll: true,
  };

  const optionsWithSelectAllCustomerCountries =
    customerCountryOption.length > 0
      ? [selectAllCustomerCountryOption, ...customerCountryOption]
      : [];


  // Customer Options
  const fetchBottlerConcentrateForecastPartnerOptions = async (bottler) => {
    try {
      const response = await ApiService.get(
        `v1/admin/userManagement/customer-selection-by-bottler?bottler=${encodeURIComponent(bottler)}`,
      );

      setPartnerOption(response.data);

    } catch (error) {
      console.error("Error fetching partner and plant options:", error);
    }
  };

  //Fetch customer country options
  const fetchCustomerCountryOptions = async (url, bottlerOwnerCodeList) => {
    const payloadData = {
      userId: userId,
      bottlerOwnerCodeList: bottlerOwnerCodeList
    }

    try {
      const response = await ApiService.post(
        url,
        payloadData,
      );

      if (response.data && response.data.length > 0) {
        setCustomerCountryOption(response.data);
        setCustomerCountryNoOptionsText(
          "Select Bottler to view Customer Country",
        );
      } else {
        setCustomerCountryOption([]);
        setCustomerCountryNoOptionsText(
          "No Customer Country found for selected Bottler",
        );
      }
    } catch (error) {
      console.error("Error fetching Country options:", error);
      setCustomerCountryOption([]);
      setCustomerCountryNoOptionsText(
        "Error fetching Country options. Please try again.",
      );
    }
  }

  const handleDelete = async (params) => {
    if (selectedRows.length === 0) {
      MessagePopup({
        icon: "warning",
        title: "No Selection",
        message: "Please select at least one row to delete.",
      });
      return;
    }

    const confirmed = await MessagePopup({
      icon: "warning",
      title: "Confirm Delete",
      message: "Are you sure you want to delete selected record?",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
    });

    if (!confirmed?.isConfirmed) return;

    const payload = selectedRows.map((row) => ({
      id: row.id,
      dataElement: row.dataElement,
      forecastTerm: row.forecastTerm
        ? getForecastTermCode(row.forecastTerm)
        : null,
      reminderPeriod: row.reminderPeriod,
      reminderPeriodDisplayName: row.reminderPeriodDisplayName,
      ndays: Number(row.ndays),
      active: row.active,

      roles: Array.isArray(row.roles) ? row.roles : [],
      userEmails: row.userEmails || "",
    }));

    try {
      await ApiService.post(`/v1/reminder-configurations/delete`, payload);

      MessagePopup({
        icon: "success",
        title: "Deleted",
        message: "Selected reminder deleted successfully.",
      });

      setSelectedRows([]);
      fetchTableData();
    } catch (error) {
      console.error("Delete failed:", error);
      MessagePopup({
        icon: "error",
        title: "Delete Failed",
        message: "Failed to delete selected reminder. Please try again.",
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setReminderDetails(null);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api && filteredData.length > 0) {
      gridRef.current.api.sizeColumnsToFit();
    }
  }, [filteredData]);

  // Fetch Reminder Period Options
  const fetchRoleOption = async () => {
    try {
      const response = await ApiService.get(
        `/v1/reminder-configurations/roles`,
      );
      setRoleOptions(response.data || []);
      const codesOnly = (response.data || []).map((item) => item.code);
      setTableRoleOptions(codesOnly);
    } catch (error) {
      console.error("Failed to fetch reminder period options:", error);
      setRoleOptions([]);
    }
  };

  return (
    <>
      <div className="container">
        {/* Form Fields */}
        <Grid container spacing={2} sx={{ mb: 1, mt: 1 }}>
          {/* Data Elements */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              disablePortal
              options={dataElementsOption}
              size="small"
              value={dataElement}
              onChange={handleDataElementChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Data Elements*"
                  InputLabelProps={{
                    shrink: true,
                    style: { transform: "translate(14px, -14px) scale(0.75)" },
                  }}
                />
              )}
            />
          </Grid>

          {/* Forecast Term */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              disablePortal
              options={forecastTermOptions}
              size="small"
              value={forecastTerm}
              onChange={(e, v) => setForecastTerm(v || null)}
              disabled={!isForecastTermEnabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    isForecastTermEnabled ? "Forecast Term*" : "Forecast Term"
                  }
                  InputLabelProps={{
                    shrink: true,
                    style: { transform: "translate(14px, -14px) scale(0.75)" },
                  }}
                />
              )}
            />
          </Grid>

          {/* Reminder Period */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              disablePortal
              options={reminderPeriodOptions}
              getOptionLabel={(option) => option?.displayName || ""}
              isOptionEqualToValue={(option, value) =>
                option?.code === value?.code
              }
              size="small"
              value={reminderPeriod}
              onChange={(e, v) => setReminderPeriod(v || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reminder Period*"
                  InputLabelProps={{
                    shrink: true,
                    style: { transform: "translate(14px, -14px) scale(0.75)" },
                  }}
                />
              )}
            />
          </Grid>

          {/* Number of Days */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Number of Days*"
              placeholder="Enter between 0 and 14"
              value={daysNumber}
              onChange={(e) => setDaysNumber(e.target.value)}
              sx={{ "& .MuiInputBase-root": { height: 37.5 } }}
              InputLabelProps={{
                shrink: true,
                style: { transform: "translate(14px, -14px) scale(0.75)" },
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2, mt: 0.25 }}>
          <Grid item xs={12} sm={6} md={3}>
            {/* Roles */}
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={2}
              id="roles-autocomplete"
              options={roleOptions}
              value={roles}
              size="small"
              getOptionLabel={(option) => option?.displayName || ""}
              isOptionEqualToValue={(option, value) =>
                option?.code === value?.code
              }
              onChange={(event, newValue) => {
                setRoles(newValue);
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option.displayName}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roles*"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                    style: {
                      transform: "translate(14px, -14px) scale(0.75)",
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      maxHeight: 120,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      overflow: "auto",
                    },
                    "& .MuiAutocomplete-input": {
                      order: -1,
                      minWidth: "100px",
                      flexGrow: 1,
                    },
                    "& .MuiChip-root": {
                      order: 0,
                    },
                  }}
                />
              )}
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip
                    key={option.code}
                    label={option.displayName}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Box display="flex" alignItems="center" gap={0}>
              <TextField
                size="small"
                fullWidth
                label="Email Recipients"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                sx={{ "& .MuiInputBase-root": { height: 37.5 } }}
                InputLabelProps={{
                  shrink: true,
                }}
                error={Boolean(emailError)}
                helperText={emailError}
              />

              <Box sx={{ ml: -0.5, mt: -0.5 }}>

                <InfoPopover
                  content={
                    <>
                      • Multiple email addresses can be provided, separated by
                      commas.
                      <br />• If no email address is provided, notifications are
                      sent to all email addresses associated with the selected
                      roles.
                    </>
                  }
                  width="260px"
                  height="auto"
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              label="Only Admin can edit"
              control={
                <Checkbox
                  checked={adminAloneEditable}
                  onChange={(e) => setAdminAloneEditable(e.target.checked)}
                />
              }
            />
          </Grid>

          {/* <Grid item>
            <ButtonComponent
              label="Clear"
              maxWidth="120px"
              bgColor="grey"
              color="white"
              onClick={handleClear}
              sx={{ mr: 2 }}
            />
          </Grid> */}
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2, mt: 0.25 }}>
          {/* Bottler */}
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              disablePortal
              options={bottlerOption}
              size="small"
              value={selectedBottler}
              onChange={handleBottlerChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bottler*"
                  InputLabelProps={{
                    shrink: true,
                    style: { transform: "translate(14px, -14px) scale(0.75)" },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>

            {dataElement === "Bottler Concentrate Forecast" &&
              /* Forecast Partner */
              (
                <Autocomplete
                  multiple
                  limitTags={1}
                  id="partner-autocomplete"
                  options={optionsWithSelectAllForecastPartners}
                  getOptionLabel={(option) =>
                    option.isSelectAll ? option.forecastPartnerDetails : option
                  }
                  value={selectedPartners}
                  onChange={(event, newValue, reason) => {
                    const isSelectAllClicked = newValue.some(
                      (item) => item.isSelectAll,
                    );
                    if (isSelectAllClicked) {
                      if (selectedPartners.length === partnerOption.length) {
                        setSelectedPartners([]);
                      } else {
                        setSelectedPartners(partnerOption);
                      }
                      setSearchForecastPartnerText(""); // Clear search text after Select All action
                    } else {
                      setSelectedPartners(newValue);
                    }
                    handleForecastPartnerChange(event, newValue, reason); // Preserve original handler
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    // Update search text only when typing or clearing the input
                    if (reason === "input" || reason === "clear") {
                      setSearchForecastPartnerText(newInputValue);
                    }
                  }}
                  inputValue={searchForecastPartnerText} // Control the input value
                  disableCloseOnSelect
                  noOptionsText="Select Bottler to view Forecast Partner"
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        style={{ marginRight: 8 }}
                        checked={
                          option.isSelectAll
                            ? selectedPartners.length === partnerOption.length
                            : selected
                        }
                      />
                      {option.isSelectAll ? option.forecastPartnerDetails : option}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Forecast Partner *"
                      variant="outlined"
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                        style: {
                          transform: "translate(14px, -14px) scale(0.75)",
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          maxHeight: 110,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          flexDirection: "row",
                          overflow: "auto",
                        },
                        "& .MuiAutocomplete-input": {
                          order: -1, // Input appears before chips
                          minWidth: "100px", // Ensure input stays visible
                          flexGrow: 1,
                          position: "relative", // Input takes available space
                        },
                        "& .MuiChip-root": {
                          order: 0, // Chips appear after input
                        },
                      }}
                    />
                  )}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                      <Chip
                        key={option.isSelectAll ? option.forecastPartnerDetails : option}
                        label={option.isSelectAll ? option.forecastPartnerDetails : option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              )}
            {dataElement !== "Bottler Concentrate Forecast" && (
              /* Bottler Owner Code */
              <TextField
                label="Bottler Owner Code"
                variant="outlined"
                size="small"
                value={selectedBottlerOwnerCode || ""}
                disabled
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  style: {
                    transform: "translate(14px, -14px) scale(0.75)",
                  },
                }}
              />
            )
            }
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {dataElement === "Bottler Concentrate Forecast" &&
              /* Plant */
              (
                <Autocomplete
                  multiple
                  limitTags={1}
                  id="plant-autocomplete"
                  options={optionsWithSelectAll}
                  getOptionLabel={(option) => option.locationDetails}
                  value={selectedPlants}
                  onChange={(event, newValue, reason) => {
                    const isSelectAllClicked = newValue.some(
                      (item) => item.isSelectAll,
                    );
                    if (isSelectAllClicked) {
                      if (selectedPlants.length === plantOption.length) {
                        setSelectedPlants([]);
                      } else {
                        setSelectedPlants(plantOption);
                      }
                      setSearchText(""); // Clear search text after Select All action
                    } else {
                      setSelectedPlants(newValue);
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    // Update search text only when typing or clearing the input
                    if (reason === "input" || reason === "clear") {
                      setSearchText(newInputValue);
                    }
                  }}
                  inputValue={searchText} // Control the input value
                  disableCloseOnSelect
                  filterSelectedOptions={false}
                  noOptionsText={plantNoOptionsText}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        style={{ marginRight: 8 }}
                        checked={
                          option.isSelectAll
                            ? selectedPlants.length === plantOption.length
                            : selected
                        }
                      />
                      {option.locationDetails}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Plant *"
                      variant="outlined"
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                        style: {
                          transform: "translate(14px, -14px) scale(0.75)",
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          maxHeight: 110,
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          flexDirection: "row",
                          overflow: "auto",
                        },
                        "& .MuiAutocomplete-input": {
                          order: -1, // Input appears before chips
                          minWidth: "100px", // Ensure input stays visible
                          flexGrow: 1,
                          position: "relative", // Input takes available space
                        },
                        "& .MuiChip-root": {
                          order: 0, // Chips appear after input
                        },
                      }}
                    />
                  )}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                      <Chip
                        key={option.locationDetails}
                        label={option.locationDetails}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              )}
            {dataElement !== "Bottler Concentrate Forecast" && (
              /* Customer Country */
              <Autocomplete
                multiple
                limitTags={1}
                id="pc-country-autocomplete"
                options={optionsWithSelectAllCustomerCountries}
                getOptionLabel={(option) => option.profitCenterDetails}
                value={selectedCustomerCountry}
                onChange={(event, newValue, reason) => {
                  const isSelectAllClicked = newValue.some(
                    (item) => item.isSelectAll,
                  );
                  if (isSelectAllClicked) {
                    if (
                      selectedCustomerCountry.length ===
                      optionsWithSelectAllCustomerCountries.length - 1
                    ) {
                      setSelectedCustomerCountry([]);
                    } else {
                      setSelectedCustomerCountry(
                        optionsWithSelectAllCustomerCountries.filter(
                          (item) => !item.isSelectAll,
                        ),
                      );
                    }
                    setSearchText(""); // Clear search text after Select All action
                  } else {
                    setSelectedCustomerCountry(newValue);
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Update search text only when typing or clearing the input
                  if (reason === "input" || reason === "clear") {
                    setSearchText(newInputValue);
                  }
                }}
                inputValue={searchText} // Control the input value
                disableCloseOnSelect
                filterSelectedOptions={false}
                noOptionsText={customerCountryNoOptionsText}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={
                        option.isSelectAll
                          ? selectedCustomerCountry.length ===
                          optionsWithSelectAllCustomerCountries.length - 1
                          : selected
                      }
                    />
                    {option.profitCenterDetails}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer Country *"
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                      style: {
                        transform: "translate(14px, -14px) scale(0.75)",
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        maxHeight: 120,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        flexDirection: "row",
                        overflow: "auto",
                      },
                      "& .MuiAutocomplete-input": {
                        order: -1,
                        minWidth: "100px",
                        flexGrow: 1,
                        position: "relative",
                      },
                      "& .MuiChip-root": {
                        order: 0,
                      },
                    }}
                  />
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      key={option.profitCenterDetails}
                      label={option.profitCenterDetails}
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            )
            }
          </Grid>
          <Grid item>
            <ButtonComponent
              label={isSubmitting ? "Submitting..." : "Submit"}
              maxWidth="120px"
              bgColor="black"
              color="white"
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          container
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" align="left" gutterBottom="true">
            Reminder Notification Table
          </Typography>

          <ButtonComponent
            label="Delete"
            maxWidth="120px"
            bgColor="black"
            color="white"
            onClick={handleDelete}
          />
        </Grid>
        {loading ? (
          <Skeleton
            variant="rectangular"
            height={"68vh"}
            animation="pulse"
            style={{ marginBottom: "16px", borderRadius: "8px" }}
          />
        ) : (
          <DataTable
            ref={gridRef}
            columns={columnDefinition}
            rows={filteredData || []}
            tableHeight={"68vh"}
            suppressMovableColumns={true}
            suppressHorizontalScroll={false}
            suppressPaginationPanel={false}
            pagination={pagination}
            paginationPageSize={paginationPageSize}
            paginationPageSizeSelector={paginationPageSizeSelector}
            onGridReady={onGrid}
            rowSelection="multiple"
            onSelectionChanged={onSelectionChanged}
            onCellValueChanged={onCellValueChanged}
          />
        )}
        <ReminderDialog
          open={open}
          onClose={handleClose}
          reminderDetails={reminderDetails}
        />
        <EditReminderDialog
          open={editOpen}
          onClose={closeEditDialog}
          data={editRowData}
          refreshTable={fetchTableData}
          roleOptions ={roleOptions}
        />
      </div>
    </>
  );
};

export default ReminderForm;
